/**
 * The single route table.
 *
 * Every route is a real URL that survives a refresh: the Flask catch-all returns `index.html` for
 * any unmatched path, and this table reads the address back. The rule editor is a child of the
 * Rules sub-tab rather than a boolean, which is what makes a deep link to an open editor
 * expressible and makes the browser back button close it.
 *
 * The shell and the jurisdictions list are in the initial chunk because that is always the entry;
 * every other top-level screen is lazy.
 */

import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useParams, type Params } from 'react-router';
import { KindeCallback } from '~/auth/kinde';
import { AppShell, type Crumb } from '~/layout/AppShell';
import { RouteError } from '~/layout/ErrorBoundary';
import { SkeletonStack } from '~/ui/Surface';
import { routes } from '~/lib/routes';
import { JurisdictionsPage } from '~/features/jurisdictions/JurisdictionsPage';
import { JurisdictionLayout } from '~/features/jurisdictions/JurisdictionLayout';
import { OverviewTab } from '~/features/jurisdictions/OverviewTab';
import { MatterLayout } from '~/features/matter/MatterLayout';
import { TimelineTab } from '~/features/matter/TimelineTab';
import { RulesTab } from '~/features/matter/RulesTab';
import { RuleEditorRoute } from '~/features/rules/RuleEditor';
import { SourcesTab } from '~/features/sources/SourcesTab';
import { GapsTab } from '~/features/matter/GapsTab';
import { NotFound } from '~/features/NotFound';

const QueuePage = lazy(() =>
  import('~/features/queue/QueuePage').then((m) => ({ default: m.QueuePage })),
);
const ReviewPage = lazy(() =>
  import('~/features/review/ReviewPage').then((m) => ({ default: m.ReviewPage })),
);
const PromptsPage = lazy(() =>
  import('~/features/prompts/PromptsPage').then((m) => ({ default: m.PromptsPage })),
);
const PromptVersionsPage = lazy(() =>
  import('~/features/prompts/PromptVersionsPage').then((m) => ({ default: m.PromptVersionsPage })),
);
const MappingsPage = lazy(() =>
  import('~/features/mappings/MappingsPage').then((m) => ({
    default: m.MappingsPage,
  })),
);
const ContradictionsPage = lazy(() =>
  import('~/features/contradictions/ContradictionsPage').then((m) => ({
    default: m.ContradictionsPage,
  })),
);
const RunsPage = lazy(() =>
  import('~/features/runs/RunsPage').then((m) => ({ default: m.RunsPage })),
);
const RunDetailPage = lazy(() =>
  import('~/features/runs/RunsPage').then((m) => ({ default: m.RunDetailPage })),
);
const AdminPage = lazy(() =>
  import('~/features/admin/AdminPage').then((m) => ({ default: m.AdminPage })),
);

const AdminSourcesPage = lazy(() =>
  import('~/features/admin/SourcesPage').then((m) => ({ default: m.AdminSourcesPage })),
);

const CoveragePage = lazy(() =>
  import('~/features/coverage/CoveragePage').then((m) => ({ default: m.CoveragePage })),
);
const RuleCataloguePage = lazy(() =>
  import('~/features/rules/CataloguePage').then((m) => ({ default: m.RuleCataloguePage })),
);
const AdminInterchangePage = lazy(() =>
  import('~/features/admin/InterchangePage').then((m) => ({ default: m.AdminInterchangePage })),
);
const AdminPeoplePage = lazy(() =>
  import('~/features/admin/PeoplePage').then((m) => ({ default: m.AdminPeoplePage })),
);
const AdminRegistersPage = lazy(() =>
  import('~/features/admin/RegistersPage').then((m) => ({ default: m.AdminRegistersPage })),
);

const RuleHistoryPage = lazy(() =>
  import('~/features/history/RuleHistoryPage').then((m) => ({ default: m.RuleHistoryPage })),
);

type FromParams<T> = T | ((params: Readonly<Params<string>>) => T);

interface DeferredProps {
  /** A function reads the crumbs off the address, because the chunk that knows them has not landed. */
  trail?: FromParams<readonly Crumb[]> | undefined;
  leaf: FromParams<string>;
  width?: 'wide' | 'reading' | undefined;
  children: ReactNode;
}

function ChunkSkeleton({ trail, leaf, width }: Omit<DeferredProps, 'children'>) {
  const params = useParams();
  return (
    <AppShell
      trail={typeof trail === 'function' ? trail(params) : trail}
      leaf={typeof leaf === 'function' ? leaf(params) : leaf}
      width={width}
    >
      <SkeletonStack rows={6} height={48} />
    </AppShell>
  );
}

export function Deferred({ trail, leaf, width, children }: DeferredProps) {
  return (
    <Suspense fallback={<ChunkSkeleton trail={trail} leaf={leaf} width={width} />}>
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Outlet />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/jurisdictions" replace /> },

      // Kinde returns the browser here. Outside the shell deliberately — there is nothing worth
      // rendering around one sentence, and the address carries a spent authorisation code.
      { path: 'auth/callback', element: <KindeCallback /> },

      { path: 'jurisdictions', element: <JurisdictionsPage />, errorElement: <RouteError /> },

      {
        path: 'jurisdictions/:code',
        element: <JurisdictionLayout />,
        errorElement: <RouteError />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: <OverviewTab /> },
          {
            path: ':matter',
            element: <MatterLayout />,
            children: [
              { index: true, element: <Navigate to="timeline" replace /> },
              { path: 'timeline', element: <TimelineTab /> },
              {
                path: 'rules',
                element: <RulesTab />,
                children: [
                  { path: 'new', element: <RuleEditorRoute /> },
                  { path: ':lane', element: <RuleEditorRoute /> },
                ],
              },
              { path: 'sources', element: <SourcesTab /> },
              { path: 'gaps', element: <GapsTab /> },
            ],
          },
        ],
      },

      {
        path: 'jurisdictions/:code/:matter/rules/:lane/history',
        element: (
          <Deferred
            trail={(params) => [
              { label: 'Legal jurisdictions', href: routes.jurisdictions() },
              { label: params.code ?? '', href: routes.overview(params.code ?? '') },
            ]}
            leaf={(params) => `${params.lane ?? ''} — history`}
            width="reading"
          >
            <RuleHistoryPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },

      // One route with an optional segment rather than two, so opening a detail pane does not
      // unmount and remount the screen. Two route objects rendering the same component are two
      // different elements to React, which discards the DOM — losing the scroll position, and
      // any state the list was holding, every time a row is expanded.
      {
        path: 'queue/:queueId?',
        element: (
          <Deferred leaf="Queue">
            <QueuePage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },

      {
        path: 'review/:proposalId?',
        element: (
          <Deferred leaf="AI research review">
            <ReviewPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },

      {
        path: 'admin',
        element: (
          <Deferred leaf="Admin" width="reading">
            <AdminPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },
      {
        path: 'admin/sources',
        element: (
          <Deferred leaf="Official sources" width="reading">
            <AdminSourcesPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },
      {
        path: 'admin/interchange',
        element: (
          <Deferred leaf="Interchange" width="reading">
            <AdminInterchangePage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },
      {
        path: 'admin/people',
        element: (
          <Deferred leaf="People" width="reading">
            <AdminPeoplePage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },
      {
        path: 'admin/registers',
        element: (
          <Deferred leaf="Registers" width="reading">
            <AdminRegistersPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },

      { path: 'prompts', element: <Navigate to="/prompts/discover" replace /> },
      {
        path: 'prompts/:promptId',
        element: (
          <Deferred leaf="AI prompts">
            <PromptsPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },
      {
        path: 'prompts/:promptId/versions',
        element: (
          <Deferred trail={[{ label: 'AI prompts', href: routes.prompts() }]} leaf="Prompt versions">
            <PromptVersionsPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },

      {
        path: 'mappings',
        element: (
          <Deferred leaf="Manage mapping">
            <MappingsPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },
      {
        path: 'rules',
        element: (
          <Deferred leaf="Rules">
            <RuleCataloguePage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },
      {
        path: 'coverage',
        element: (
          <Deferred leaf="Coverage">
            <CoveragePage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },
      {
        path: 'coverage/:matter',
        element: (
          <Deferred leaf="Coverage">
            <CoveragePage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },
      {
        path: 'contradictions',
        element: (
          <Deferred leaf="Contradictions">
            <ContradictionsPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },

      {
        path: 'runs',
        element: (
          <Deferred leaf="Run history">
            <RunsPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },
      {
        path: 'runs/:runId',
        element: (
          <Deferred
            trail={[{ label: 'Run history', href: routes.runs() }]}
            leaf={(params) => `Run ${params.runId ?? ''}`}
          >
            <RunDetailPage />
          </Deferred>
        ),
        errorElement: <RouteError />,
      },

      { path: '*', element: <NotFound /> },
    ],
  },
]);
